import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import navigation from 'menu-items';

// Build a flat map: { '/cms/blog': { title, parents: ['CMS', 'Content Management'] } }
function buildRouteMap(items) {
    const map = {};

    items?.forEach((group) => {
        group.children?.forEach((collapse) => {
            if (collapse.type === 'item') {
                map[collapse.url] = {
                    title: collapse.title,
                    parents: [group.title],
                };
            } else if (collapse.type === 'collapse') {
                collapse.children?.forEach((item) => {
                    if (item.type === 'item') {
                        map[item.url] = {
                            title: item.title,
                            parents: [group.title, collapse.title],
                        };
                    }
                });
            }
        });
    });

    return map;
}

// Extra labels for sub-routes not in menu
const SUB_ROUTE_LABELS = {
    create: 'Create',
    edit: 'Edit',
    detail: 'Detail',
};

export default function Breadcrumbs() {
    const location = useLocation();
    const pathname = location.pathname;

    const routeMap = buildRouteMap(navigation?.items);

    // Try exact match first
    const exact = routeMap[pathname];

    if (exact) {
        const items = [
            { title: <Link to="/"><HomeOutlined /></Link> },
            ...exact.parents.map((p) => ({ title: p })),
            { title: exact.title },
        ];
        return <Breadcrumb items={items} className="mb-4" />;
    }

    // Try to match dynamic/nested routes
    // e.g. /cms/blog/create, /cms/blog/edit/123, /cms/blog/detail/123
    // e.g. /coach/detail/123, /cms/user-report/detail/123
    const segments = pathname.split('/').filter(Boolean);

    // Find the closest parent route in the map
    let parentEntry = null;
    let parentUrl = null;

    // Walk back segments to find a known parent
    for (let i = segments.length - 1; i >= 1; i--) {
        const candidate = '/' + segments.slice(0, i).join('/');
        if (routeMap[candidate]) {
            parentEntry = routeMap[candidate];
            parentUrl = candidate;
            break;
        }
    }

    // Determine sub-page label from remaining segments after parent
    const lastSegment = segments[segments.length - 1];
    // If last segment looks like an ID (24-char hex or numeric), go one before
    const isId = /^[a-f0-9]{20,}$/i.test(lastSegment) || /^\d+$/.test(lastSegment);
    const actionSegment = isId ? segments[segments.length - 2] : lastSegment;
    const subLabel = SUB_ROUTE_LABELS[actionSegment] || actionSegment?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    if (parentEntry) {
        const items = [
            { title: <Link to="/"><HomeOutlined /></Link> },
            ...parentEntry.parents.map((p) => ({ title: p })),
            { title: <Link to={parentUrl}>{parentEntry.title}</Link> },
            { title: subLabel },
        ];
        return <Breadcrumb items={items} className="mb-4" />;
    }

    // Fallback: build from raw segments
    const fallbackItems = [
        { title: <Link to="/"><HomeOutlined /></Link> },
        ...segments.map((seg, idx) => {
            const url = '/' + segments.slice(0, idx + 1).join('/');
            const label = seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            const isLast = idx === segments.length - 1;
            const isIdSeg = /^[a-f0-9]{20,}$/i.test(seg) || /^\d+$/.test(seg);
            if (isIdSeg) return null;
            return { title: isLast ? label : <Link to={url}>{label}</Link> };
        }).filter(Boolean),
    ];

    return <Breadcrumb items={fallbackItems} className="mb-4" />;
}
